import { Platform } from 'react-native';

import { dataService } from '@/services/data-service';
import { logger } from '@/utils/logger';

/**
 * Issue #89: iOS badge gets stuck showing a stale number even after the
 * urgent work order that triggered it is completed. Root cause: the push
 * payload sends `badge: 1` which iOS *increments* (it's actually a set-to-N
 * semantically, but our server sends the same value for each push so it's
 * effectively additive in practice), and the client only cleared the badge
 * on mount + notification-tap — never when the underlying WO was closed.
 *
 * Fix: treat the badge as a *computed* count of "urgent work orders that
 * still need my attention". Whenever the app foregrounds or mounts, we
 * query that count and call `setBadgeCountAsync(n)` to reconcile. Pushes
 * still bump the badge on the lock screen (so the user notices the new
 * urgent WO), but the app corrects the count as soon as it's opened.
 *
 * Kept as a thin service so the NotificationProvider stays focused on
 * listener lifecycle and so tests can exercise the query shape without
 * rendering the provider.
 */

export interface UrgentBadgeQuery {
	/** Filters that reflect "urgent, still needs me". */
	assignedTo: 'me';
	status: 'open,in_progress';
	priority: 'urgent';
	/** limit=1 — we only need pagination.total, not actual rows. */
	limit: 1;
}

export const URGENT_BADGE_QUERY: UrgentBadgeQuery = {
	assignedTo: 'me',
	status: 'open,in_progress',
	priority: 'urgent',
	limit: 1,
};

/**
 * Fetches the current urgent WO count for the signed-in user. Returns `null`
 * if the request fails — callers should leave the badge untouched in that
 * case rather than flashing a wrong number.
 */
export async function getUrgentBadgeCount(): Promise<number | null> {
	try {
		const response = await dataService.getWorkOrders(URGENT_BADGE_QUERY);
		return typeof response.total === 'number' ? response.total : null;
	} catch (error) {
		logger.log('getUrgentBadgeCount failed:', error);
		return null;
	}
}

/**
 * Reconcile the OS badge to the current urgent count. Web has no concept of
 * a badge we can set programmatically in Expo, so it's a no-op there.
 */
export async function reconcileUrgentBadge(): Promise<void> {
	if (Platform.OS === 'web') return;
	const count = await getUrgentBadgeCount();
	if (count === null) return;
	try {
		// Lazy-require so the tests can exercise the query shape without
		// pulling in expo-notifications (which errors under vitest's node
		// environment because it assumes the Expo native runtime).
		const Notifications = await import('expo-notifications');
		await Notifications.setBadgeCountAsync(count);
	} catch (error) {
		logger.log('setBadgeCountAsync failed:', error);
	}
}
